'use client'

import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useVoiceAgents } from '@/hooks/agents/voice'
import type { AIAgentData } from '@/lib/pbx/flow/types'
import { Loader2 } from 'lucide-react'
import { useTranslations } from "next-intl"


interface AIAgentPanelProps {
  data: AIAgentData
  onUpdate: (data: Partial<AIAgentData>) => void
}

export function AIAgentPanel({ data, onUpdate }: AIAgentPanelProps) {
  const tUi = useTranslations("Pbx.PbxFlowEditorPanelsAIAgentPanel")

  const { agents, isLoading } = useVoiceAgents()

  // Filter only active agents
  const activeAgents = agents.filter(agent =>
    agent.status === 'active' && agent.external_id
  )

  const handleAgentSelect = (agentId: string) => {
    const selectedAgent = agents.find(a => a.id === agentId)
    if (selectedAgent) {
      onUpdate({
        agent_id: selectedAgent.id,
        external_agent_id: selectedAgent.external_id,
        agent_name: selectedAgent.name,
      })
    }
  }

  return (
    <div className="p-4 space-y-4">
      <div className="space-y-2">
        <Label htmlFor="agent_select"><span key={tUi("voiceAgent")}>{tUi("voiceAgent")}</span></Label>
        {isLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span><span key={tUi("loadingAgents")}>{tUi("loadingAgents")}</span></span>
          </div>
        ) : activeAgents.length === 0 ? (
          <div className="text-sm text-yellow-600 py-2">
            <span key={tUi("noActiveVoiceAgentsFoundCreateOneIn")}>{tUi("noActiveVoiceAgentsFoundCreateOneIn")}</span></div>
        ) : (
          <Select
            value={data.agent_id || ''}
            onValueChange={handleAgentSelect}
          >
            <SelectTrigger id="agent_select">
              <SelectValue placeholder={tUi("selectAVoiceAgent")} />
            </SelectTrigger>
            <SelectContent>
              {activeAgents.map((agent) => (
                <SelectItem key={agent.id} value={agent.id}>
                  {agent.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <p className="text-xs text-muted-foreground">
          <span key={tUi("selectAVoiceAgentToHandleIncomingCalls")}>{tUi("selectAVoiceAgentToHandleIncomingCalls")}</span></p>
      </div>

      <div className="flex items-center justify-between">
        <div className="space-y-0.5">
          <Label><span key={tUi("escalateToHuman")}>{tUi("escalateToHuman")}</span></Label>
          <p className="text-xs text-muted-foreground">
            <span key={tUi("allowAIToTransferToHumanOperator")}>{tUi("allowAIToTransferToHumanOperator")}</span></p>
        </div>
        <Switch
          checked={data.human_handover_enabled ?? true}
          onCheckedChange={(checked) => onUpdate({ human_handover_enabled: checked })}
        />
      </div>

      <div className="p-3 bg-muted rounded-lg text-sm">
        <p key={(data.human_handover_enabled ?? true)
            ? tUi("connectTheEscalateToHumanOutputToAn")
            : tUi("aIWillNotBeAbleToTransferCalls")} className="text-muted-foreground">
          {(data.human_handover_enabled ?? true)
            ? tUi("connectTheEscalateToHumanOutputToAn")
            : tUi("aIWillNotBeAbleToTransferCalls")}
        </p>
      </div>
    </div>
  )
}
